import { CartAndLineItemGroups, LineItem, LineItemGroup } from '@/types/commercetools';
import { CartItem } from '@/hooks/useCartHooks';
import { parseCompositionsJson, parseTrackingJson } from '../cartHelpers';
import { getBasketItemPrices } from '../getBasketItemPrices';
import { getAppearanceId, getProductTypeId, getSizeId } from '../lineItems/lineItemHelpers';
import { tracker } from './tracker';
import { CartFeatureDetail } from './types';

const getProducts = (cartItems: CartItem[]) => {
  return cartItems.map((cartItem) => {
    const singlePrice =
      cartItem.quantity > 0
        ? Math.round((cartItem.price * 100) / cartItem.quantity) / 100
        : cartItem.price;
    return {
      designs: cartItem.compositions,
      ...cartItem.trackingData,
      productTypeId: cartItem.productTypeId.toString(),
      appearanceId: cartItem.appearanceId,
      price: singlePrice,
      amount: cartItem.quantity,
      productId: cartItem.productId?.toString(),
      articleId: cartItem.articleId,
      articleName: cartItem.articleName,
      sellableId: cartItem.sellableId,
      sizeId: cartItem.sizeId,
      shopId: cartItem.shopId,
    };
  });
};

const getLineItemProduct = (
  lineItem: LineItem,
  lineItemGroup: LineItemGroup,
  amount = lineItem.quantity
) => {
  const { singlePrice } = getBasketItemPrices(lineItemGroup);
  return {
    designs: parseCompositionsJson(lineItem),
    ...parseTrackingJson(lineItem),
    productTypeId: getProductTypeId(lineItem).toString(),
    appearanceId: getAppearanceId(lineItem).toString(),
    price: singlePrice / 100,
    amount,
    productId: lineItem.productId,
    articleId: lineItem.custom.fields.sprdCompositionId,
    articleName: lineItem.name.en,
    sellableId: lineItem.custom.fields.sprdSellableId,
    sizeId: getSizeId(lineItem).toString(),
    shopId: lineItemGroup.shopId,
  };
};

export const trackCartAddProduct = (featureDetail: CartFeatureDetail, cartItems: CartItem[]) => {
  tracker.trackFeature({
    featureUsed: 'cart-addition',
    detail: featureDetail,
    product: getProducts(cartItems),
  });
};

export const trackCartAction = (
  featureUsed: 'cart-addition' | 'cart-removal',
  lineItemGroup: LineItemGroup,
  lineItem: LineItem,
  quantityDiff = 1
) => {
  tracker.trackFeature({
    featureUsed,
    detail: 'minibasket',
    product: getLineItemProduct(lineItem, lineItemGroup, quantityDiff),
  });
};

export const trackCartDeleteGroup = (lineItemGroup: LineItemGroup) => {
  const product = lineItemGroup.lineItems.map((lineItem) =>
    getLineItemProduct(lineItem, lineItemGroup)
  );
  tracker.trackFeature({
    featureUsed: 'cart-removal',
    detail: 'minibasket',
    product,
  });
};

// Sets the window.analytics.cart object
export const trackCartData = (cartAndLineItemGroups: CartAndLineItemGroups) => {
  const cartItems = cartAndLineItemGroups.lineItemGroups.map((lineItemGroup) => {
    const { singlePrice, singleDiscount } = getBasketItemPrices(lineItemGroup);
    const lineItem = lineItemGroup.lineItems[0]!;

    return {
      designs: parseCompositionsJson(lineItem),
      ...parseTrackingJson(lineItem),
      productTypeId: lineItemGroup.productTypeId,
      appearanceId: lineItemGroup.appearanceId,
      articleId: lineItem.custom.fields.sprdCompositionId,
      //TODO: Add articleName when we can store PDP title for a cart item for MP products. Otherwise articleName for mp AND cyo should be undefined https://jira.spreadomat.net/browse/DEV-222907
      articleName: undefined,
      sellableId: lineItemGroup.sellableId,
      productId: lineItemGroup.productId,
      shopId: lineItemGroup.shopId,
      sizes: lineItemGroup.lineItems.map((lineItem) => {
        return {
          sizeId: getSizeId(lineItem),
          quantity: lineItem.quantity,
        };
      }),
      price: singlePrice / 100,
      discountedPrice: (singlePrice - singleDiscount) / 100,
    };
  });

  tracker.trackCart({ cart: { id: cartAndLineItemGroups.cart.id, cartItems: cartItems } });
};

// to set the cart id in the analytics data already before the full cart data gets tracked
// this is required for the first cart addition
export const trackCartId = (cartId: string) => {
  tracker.trackCart({ cart: { id: cartId } });
};
